import React, { useState } from 'react';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import { Container, TextInput, Button, Select, Alert, List } from '@pella/sash-framework';
import { savePostalCode, setCookie, getCookie } from '../../../utilities/functions';
import { getMarket, getBranch, getShowroom, getServiceInfo } from '../../../../actions/location-actions';
import { useFooter } from '../../../utilities/static-queries/footer';
import EloquaForm from '../../eloqua-form';
import { isValidEmail, isValidPostal } from '../../../utilities/validations';
import { eloquaTrackingUrl } from '../../../utilities/constants';

const LeadGenForm = () => {
	const data = useFooter();
	const dispatch = useDispatch();
	const [values, setValues] = useState({ email: '', zip: '', accountType: '' });
	const [success, setSuccess] = useState(false);
	const [errors, setErrors] = useState([]);

	if (!success && getCookie('newsletterOptIn') != null) return null;

	const onInputChange = (name, value) => setValues({ ...values, [name]: value });

	const validateFields = () => {
		let errors = [];

		if (!isValidEmail(values.email)) {
			errors.push({ field: 'email', message: data.errors.find((error) => error.field_id === 'email')?.error_2 });
		}
		if (!values.zip || !isValidPostal(values.zip)) {
			errors.push({ field: 'zip', message: data.errors.find((error) => error.field_id === 'zip')?.error_2 });
		}
		if (!values.accountType) {
			errors.push({ field: 'accountType', message: data.errors.find((error) => error.field_id === 'accountType')?.error_2 });
		}

		if (errors.length > 0) {
			setErrors(errors);
			return false;
		}

		return true;
	};

	const onSubmit = (e) => {
		e.preventDefault();

		if (validateFields()) {
			const formData = new FormData(e.currentTarget);

			savePostalCode(values.zip);
			dispatch(getMarket(values.zip));
			dispatch(getBranch(values.zip));
			dispatch(getShowroom(values.zip));
			dispatch(getServiceInfo(values.zip));

			axios
				.post(eloquaTrackingUrl, formData)
				.then((res) => {
					if (res.status === 200) {
						setSuccess(true);
						setErrors([]);
						setCookie('newsletterOptIn', true);
					}
				})
				.catch();
		}
	};

	const onAlertClose = () => setErrors([]);

	return (
		<div
			id='footer-signup'
			className='leadGen bg-neutral-50 py-8 xl:py-12'
		>
			<Container>
				<div className='grid auto-rows-[minmax(min-content,max-content)] grid-cols-1 items-center xl:grid-cols-[225px_1fr] xl:gap-x-[3.75rem]'>
					{data.newsletter && <p className='col-start-1 row-start-1 mb-7 text-center text-2xl font-bold !leading-none md:text-3xl xl:row-span-2 xl:mb-0 xl:text-left'>{data.newsletter}</p>}

					{success && data.success_message && (
						<Alert
							className='col-start-1 row-start-2 mb-4 xl:col-start-2 xl:row-start-1'
							type='success'
							hideClose
						>
							{data.success_message}
						</Alert>
					)}
					{errors?.[0] && (
						<Alert
							type='error'
							onClose={onAlertClose}
							className='col-start-1 row-start-2 mb-4 xl:col-start-2 xl:row-start-1'
						>
							<List className='m-0 text-sm'>
								{errors.map((error, index) => (
									<li key={index}>{error.message}</li>
								))}
							</List>
						</Alert>
					)}

					{!success && (
						<EloquaForm
							elqFormName='email-footer-signup-1572638485532'
							onFormSubmit={onSubmit}
							className='col-start-1 row-start-3 grid grid-cols-1 items-end gap-y-4 md:grid-cols-[1fr_1fr_1fr_100px] md:gap-x-5 md:gap-y-0 xl:col-start-2 xl:row-start-2 xl:gap-x-4 [&>*]:mb-0'
						>
							<TextInput
								type='email'
								id='footerEmail'
								labelText='Email Address'
								onChange={(e) => onInputChange('email', e.target.value)}
								error={errors?.find((error) => error.field === 'email') ? true : false}
								value={values.email}
								autoComplete='email'
								required
							/>

							<TextInput
								id='footerZip'
								labelText='Zip Code'
								onChange={(e) => onInputChange('zip', e.target.value)}
								error={errors?.find((error) => error.field === 'zip') ? true : false}
								autoComplete='postal-code'
								value={values.zip}
								required
							/>

							{data.customer_type_options?.[0] && (
								<Select
									id='accountType'
									labelText='Customer Type'
									onChange={(e) => onInputChange('accountType', e.target.value)}
									error={errors?.find((error) => error.field === 'accountType') ? true : false}
									autoComplete='off'
									required
								>
									<option value=''>--- Please Select ---</option>
									{data.customer_type_options.map((opt, index) => (
										<option
											value={opt}
											key={index}
										>
											{opt}
										</option>
									))}
								</Select>
							)}

							<Button
								type='submit'
								color='primary-alt'
								data-gtm-linktype='button'
								data-gtm-linktext='Email Sign Up'
								className='md:h-[54px] md:py-0'
							>
								Submit
							</Button>
						</EloquaForm>
					)}
				</div>
			</Container>
		</div>
	);
};

export default LeadGenForm;
